import { createEffect, onCleanup } from "solid-js"
import { BufferGeometry, Vector3 } from "three"
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js"
import { onCleanupRemoveAndDispose } from "../../../../../rx/cleanup/onCleanupRemoveAndDispose"
import { createSharedTileGraphNodeCollectionMemo } from "../../../../../rx/memo/createSharedTileGraphNodeCollectionMemo"
import useGridWidthHeight from "../../../../../rx/memo/useGridWidthHeight"
import { useNodeNeighborBaseWaterElevationReactiveMap } from "../../../../../rx/memo/useNodeNeighborBaseWaterElevationReactiveMap"
import { createTriggerSignal } from "../../../../../rx/signal/create_trigger_signal"
import removeItem from "../../../../array/remove_item"
import type { Engine } from "../../../../core/engine/Engine.type"
import { HALF_PI } from "../../../../core/util/math/Math"
import { mountOnFrameUpdateToObject3D } from "../../../../event/GameObject3D/GameObject3DFrameUpdate/GameObject3DFrameUpdate"
import { VERTICES_PER_TILE } from "../../../consts"
import useMainScene from "../../../fn/useMainScene"
import InwardPointedPlaneGeometry from "../../mesh/InwardPointedPlaneGeometry.class"
import { Water } from "../../mesh/Water"
import getTerrainMesh1Z from "../../TerrainMesh1V2/getTerrainMesh1Z"
import { getWaterNormals1024Texture } from "../textures/getWaterNormals1024TexturePromise"

const size = (VERTICES_PER_TILE * 1)
const WATER_SURFACE_SEGMENTS = 16

/**
 * fork of createWaterPlaneMeshV2
 */
export default function createWaterPlaneMeshV8(
  engine: Engine,
  width: number,
  height: number,
) {
  const scene = useMainScene(engine)
  const getGridSize = useGridWidthHeight(engine)
  const getTileGraphNodesList = createSharedTileGraphNodeCollectionMemo(engine)

  const [trackGeometryList, dirtyGeometryList] = createTriggerSignal()
  const geometryList: Array<InwardPointedPlaneGeometry> = []

  const neighborBaseElevationReactiveMap = useNodeNeighborBaseWaterElevationReactiveMap(engine)

  const textureSize = 512

  // Create water mesh with shader-based material
  const water = new Water(new BufferGeometry(), {
    textureWidth: textureSize,
    textureHeight: textureSize,
    waterNormals: getWaterNormals1024Texture(),
    sunDirection: new Vector3(Math.SQRT1_2, Math.SQRT1_2, 0.0),
    sunColor: 0xffffff,
    waterColor: 0x001e0f, // Dark blue-green water color
    distortionScale: 3.7,
    fog: scene.fog !== undefined,
    alpha: 0.5, // Slightly transparent
  })

  water.name = 'WaterPlaneV8'
  water.rotation.x = -HALF_PI

  // Position water plane at center of terrain
  const worldX = ((width - 1) * VERTICES_PER_TILE) / 2
  const worldY = ((height - 1) * VERTICES_PER_TILE) / 2
  const rh = 0
  water.position.set(worldX, rh, worldY)

  // Create geometry effect for dynamic water tiles
  createEffect(() => {
    const { width: gridWidth, height: gridHeight } = getGridSize()
    const xOffset = (gridWidth - 1) / 2
    const yOffset = (gridHeight - 1) / 2

    createEffect(() => {
      const tileNodeList = getTileGraphNodesList()
      tileNodeList.forEach((node) => {
        const nodeIndex = node.index

        createEffect(() => {
          if (node.water_prox()) {
            const geometry = new InwardPointedPlaneGeometry(size, size, WATER_SURFACE_SEGMENTS, WATER_SURFACE_SEGMENTS)
            const floor_e_value = node.floor_e()
            const water_e_value = node.water_e()
            const baseElevationsCollection = neighborBaseElevationReactiveMap.get(nodeIndex)
            if (baseElevationsCollection && (water_e_value > floor_e_value || true)) {
              const positionAttr = geometry.attributes.position
              const positionAttrLen = positionAttr.count
              for (let i = 0; i < positionAttrLen; i++) {
                const x = positionAttr.getX(i)
                const y = positionAttr.getY(i)
                const z = getTerrainMesh1Z(baseElevationsCollection, x, y)
                positionAttr.setZ(i, z)
              }
  
              geometry.translate((node.x - xOffset) * size, -(node.y - yOffset) * size, 0)
              geometryList.push(geometry)
              dirtyGeometryList()
            }
            onCleanup(() => {
              removeItem(geometryList, geometry)
              dirtyGeometryList()
            })
          }
        })
      })
    })
  })

  const emptyGeometry = new BufferGeometry()

  // Merge geometries effect
  createEffect(() => {
    trackGeometryList()
    if (geometryList.length > 0) {
      console.time('mergeGeometries')
      const mergedGeometry = mergeGeometries(geometryList)
      console.timeEnd('mergeGeometries')
      water.geometry = mergedGeometry
      water.visible = true
    } else {
      water.geometry = emptyGeometry
      water.visible = false
    }
  })

  const uTime = water.material.uniforms.time

  // Animate water shader time uniform for wave animation
  mountOnFrameUpdateToObject3D(engine, water, (dt: number) => {
    // Update time uniform for animated waves
    // The Water shader uses this for scrolling normal maps
    uTime.value += dt / 8000
  })

  scene.add(water)
  onCleanupRemoveAndDispose(water)
}